import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import type { GridPaginationModel } from '@mui/x-data-grid-pro'
import { NoAccessCard } from 'src/components/shared/NoAccessCard'
import { SectionCard } from 'src/components/shared/SectionCard'
import { AdminFetchRetryConfigs } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { RETRY_CONFIG_DEFAULT_LIMIT } from 'src/constants/scheduler/constants'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { useHasPermission } from 'src/hooks/useHasPermission'
import type { RetryConfigSortDirection } from 'src/interfaces/retryConfig'
import { getRetryConfigsError } from 'src/selectors/retryConfig'
import { ScheduledActionsTable } from './ScheduledActionsTable'
import { ScheduledActionsToolbar } from './ScheduledActionsToolbar'

const ScheduledActionsTabComponent = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const listError = useSelector(getRetryConfigsError)

  // The list endpoint requires a partner, and the partner picker is GET /partners. Without
  // `partners_view` nobody can pick one, so the tab renders a no-access card instead (spec §6).
  const canViewPartners = useHasPermission(InternalPermission.PartnersView)
  // Without `markets_view` the market filter is hidden, so a `partnerMarketId` left in a shared
  // URL must not silently narrow the list to a market the user can neither see nor clear.
  const canViewMarkets = useHasPermission(InternalPermission.MarketsView)

  // Bumped by Refresh — a dependency of the fetch effect, so it re-triggers the request.
  const [refreshToken, setRefreshToken] = useState<number>(0)

  const partnerId = searchParams.get('partnerId')
  const partnerMarketId = searchParams.get('partnerMarketId')
  const onlyScheduled = searchParams.get('onlyScheduled') === 'true'
  const sortDirection: RetryConfigSortDirection =
    searchParams.get('sort') === 'DESC' ? 'DESC' : 'ASC'

  const paginationModel = useMemo(() => {
    const pageSize = Number(searchParams.get('limit')) || RETRY_CONFIG_DEFAULT_LIMIT
    const offset = Number(searchParams.get('offset')) || 0

    return { page: Math.floor(offset / pageSize), pageSize }
  }, [searchParams])

  // Builds the next query string from the FUNCTIONAL form's `previous` argument, not the
  // `searchParams` captured in this render's closure. `setSearchParams` replaces the whole query
  // string rather than merging, so two calls into this handler in one tick (or two handlers
  // called back-to-back from one event, as the partner/market change used to) would otherwise
  // both build from the same stale base and the second call's result would silently discard the
  // first's.
  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (previousParams) => {
          const nextParams = new URLSearchParams(previousParams)

          Object.entries(updates).forEach(([key, value]) => {
            if (value === null) {
              nextParams.delete(key)

              return
            }

            nextParams.set(key, value)
          })

          return nextParams
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  // Every filter change resets to the first page: staying on page 4 of a different partner's
  // list shows an empty grid the user cannot explain.
  const handlePartnerChange = useCallback(
    (nextPartnerId: string | null) => {
      updateParams({ partnerId: nextPartnerId, partnerMarketId: null, offset: null })
    },
    [updateParams],
  )

  const handleMarketChange = useCallback(
    (nextMarketId: string | null) => {
      updateParams({ partnerMarketId: nextMarketId, offset: null })
    },
    [updateParams],
  )

  const handleOnlyScheduledChange = useCallback(
    (nextOnlyScheduled: boolean) => {
      updateParams({ onlyScheduled: nextOnlyScheduled ? 'true' : null, offset: null })
    },
    [updateParams],
  )

  const handleSortDirectionChange = useCallback(
    (direction: RetryConfigSortDirection) => {
      updateParams({ sort: direction, offset: null })
    },
    [updateParams],
  )

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      updateParams({
        limit: String(model.pageSize),
        offset: String(model.page * model.pageSize),
      })
    },
    [updateParams],
  )

  const handleRefresh = useCallback(() => {
    setRefreshToken((previousToken) => {
      return previousToken + 1
    })
  }, [])

  // The single fetch effect. No partner ⇒ no request at all: the endpoint's `partnerId` is
  // `@IsNotEmpty`, so a request without one is a guaranteed 400.
  useEffect(() => {
    if (!isAuthenticated || !canViewPartners || !partnerId) {
      return
    }

    asyncDispatchWithErrorToast(
      AdminFetchRetryConfigs.request({
        partnerId,
        partnerMarketId: canViewMarkets ? partnerMarketId : null,
        onlyScheduled,
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
        sortDirection,
      }),
    )
  }, [
    asyncDispatchWithErrorToast,
    canViewMarkets,
    canViewPartners,
    isAuthenticated,
    onlyScheduled,
    paginationModel.page,
    paginationModel.pageSize,
    partnerId,
    partnerMarketId,
    refreshToken,
    sortDirection,
  ])

  if (!canViewPartners) {
    return (
      <NoAccessCard
        message="Picking a partner requires access to partners."
        dataTestId="schedulers-partners-no-access"
      />
    )
  }

  return (
    <Fragment>
      <ScheduledActionsToolbar
        partnerId={partnerId}
        partnerMarketId={partnerMarketId}
        onlyScheduled={onlyScheduled}
        onPartnerChange={handlePartnerChange}
        onMarketChange={handleMarketChange}
        onOnlyScheduledChange={handleOnlyScheduledChange}
        onRefresh={handleRefresh}
      />
      {listError ? (
        <SectionCard title="Scheduled actions">
          <Typography variant="body2" css={tw`text-critical-500`}>
            {listError}
          </Typography>
        </SectionCard>
      ) : null}
      <ScheduledActionsTable
        hasPartner={Boolean(partnerId)}
        paginationModel={paginationModel}
        sortDirection={sortDirection}
        onPaginationModelChange={handlePaginationModelChange}
        onSortDirectionChange={handleSortDirectionChange}
      />
    </Fragment>
  )
}

export const ScheduledActionsTab = memo(ScheduledActionsTabComponent)
